import { logger } from '@zep/logger/runtime';

export type ValidateImageParams = {
  file: File | File[];
  validation: {
    mbSize: number;
    extensions: string[];
    countLimit: number;
    maxDimension: number;
  };
};

interface ImageValidationResult {
  isValid: boolean;
  type?: 'count' | 'size' | 'extension' | 'dimension';
}

type RejectionLog = {
  reason: NonNullable<ImageValidationResult['type']>;
} & Record<string, string | number>;

// 검증 실패는 사용자에게 다이얼로그로만 드러나므로 운영에서 원인을 조회할 수 있게 남긴다.
const logRejection = (attributes: RejectionLog) => {
  logger.debug('validateImage: rejected', attributes);
};

export const validateImage = async ({
  validation,
  file,
}: ValidateImageParams): Promise<ImageValidationResult> => {
  const { mbSize, extensions, countLimit, maxDimension } = validation;

  const files = Array.isArray(file) ? file : [file];

  // 개수 초과
  if (files.length > countLimit) {
    logRejection({ reason: 'count', fileCount: files.length, countLimit });
    return {
      isValid: false,
      type: 'count',
    };
  }

  for (const file of files) {
    const fileSize = file.size;
    // 브라우저에 따라 type 에 응답 Content-Type 파라미터(;charset=… 등)가 남으므로 떼고 비교한다.
    const mimeType = file.type.split(';')[0].trim().toLowerCase();
    const fileExtension = mimeType.split('/').pop() || '';

    // 이미지 확장자 불일치
    if (!extensions.includes(fileExtension)) {
      logRejection({
        reason: 'extension',
        fileType: file.type,
        fileExtension,
        allowedExtensions: extensions.join(','),
      });
      return {
        isValid: false,
        type: 'extension',
      };
    }

    // 파일 용량 초과
    if (fileSize > mbSize * 1024 * 1024) {
      logRejection({ reason: 'size', fileType: file.type, fileSize, mbSize });
      return {
        isValid: false,
        type: 'size',
      };
    }

    // 이미지 크기 초과
    // 디코딩된 비트맵은 원본 픽셀만큼 메모리를 점유하므로 검사 직후 즉시 해제한다.
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    bitmap.close();

    if (width >= maxDimension || height >= maxDimension) {
      logRejection({
        reason: 'dimension',
        fileType: file.type,
        width,
        height,
        maxDimension,
      });
      return {
        isValid: false,
        type: 'dimension',
      };
    }
  }

  return {
    isValid: true,
  };
};
